import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import {
  postMember,
  patchMember,
  deleteMember,
  passwordReset as patchPasswordReset,
  postImportMembers,
  getOccupationList,
  deleteMemberMfa,
  postInviteMembers,
} from '../../apis/member'
import {
  addListMember,
  updateListMember,
  deleteListMember,
  addListMultiMembers,
} from './listSlice'
import { setErrors } from '../common/errorsSlice'
import {
  Member,
  AddMemberRequest,
  EditMemberRequest,
  AddMultiMembersRequest,
  Occupation,
  GetOccupationListResponse,
  PostInviteMembersRequest,
} from '../../types/member'
import { findIndex } from 'lodash'

interface MemberState {
  members: Member[]
  occupationList: Occupation[]
  isLoading: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  csvErrors: any
}

interface ErrorCallback {
  errors: {
    column_name: string,
    message: string[],
  }[]
}

const initialState: MemberState = {
  members: [],
  occupationList: [],
  isLoading: false,
  csvErrors: {},
}

const memberSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    postMemberSuccess: (state, action: PayloadAction<Member>) => {
      state.members.push(action.payload)
      state.isLoading = false
      return state
    },
    postImportMembersSuccess: (state) => {
      state.csvErrors = {}
      state.isLoading = false
      return state
    },
    patchMemberSuccess: (state, action: PayloadAction<Member>) => {
      state.isLoading = false
      const index = findIndex(state.members, { id: action.payload.id })
      const editedMember = {
        ...state.members[index],
        ...action.payload,
      }
      state.members[index] = editedMember
      return state
    },
    deleteMemberMfaSuccess: (state) => {
      state.isLoading = false
      return state
    },
    deleteMemberSuccess: (state, action: PayloadAction<number>) => {
      state.members = state.members.filter((member) => member.id !== action.payload)
      state.isLoading = false
      return state
    },
    passwordResetSuccess: (state) => {
      state.isLoading = false
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setImportErrors: (state, action: PayloadAction<any>) => {
      state.csvErrors = {}
      if (action.payload.errors[0].message === '.line_errors') {
        state.csvErrors = action.payload.errors[0].details
      }
      state.isLoading = false
      return state
    },
    getOccupationListSuccess: (state, action: PayloadAction<GetOccupationListResponse>) => {
      state.occupationList = action.payload.occupation_list
      state.isLoading = false
      return state
    },
    resetState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestStart,
  postMemberSuccess,
  patchMemberSuccess,
  deleteMemberSuccess,
  deleteMemberMfaSuccess,
  passwordResetSuccess,
  requestFailure,
  postImportMembersSuccess,
  setImportErrors,
  getOccupationListSuccess,
  resetState,
} = memberSlice.actions

export const memberReducer = memberSlice.reducer

export const createMember = (
  postParams: AddMemberRequest,
  callback: () => void,
  errorCallback?: () => void,
): AppThunk => async (dispatch) => {
  postMember(postParams)
    .then((result) => {
      dispatch(addListMember(result))
      dispatch(postMemberSuccess(result))
      callback()
    })
    .catch(() => {
      dispatch(requestFailure())
      if (errorCallback) errorCallback()
    })
}

export const updateMember = (
  memberId: number,
  params: EditMemberRequest,
  callback: () => void,
  errorCallback?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchMember(memberId, params)
    .then((result) => {
      dispatch(updateListMember(result))
      dispatch(patchMemberSuccess(result))
      callback()
    })
    .catch(() => {
      if (errorCallback) errorCallback()
    })
}

export const destroyMember = (
  memberId: number,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  deleteMember(memberId)
    .then(() => {
      dispatch(deleteListMember(memberId))
      dispatch(deleteMemberSuccess(memberId))
      callback()
    })
    .catch(() => {
      dispatch(requestFailure())
    })
}

export const passwordReset = (
  memberId: number,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchPasswordReset(memberId)
    .then(() => {
      dispatch(passwordResetSuccess())
      if (callback) callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const destroyMemberMfa = (
  memberId: number,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  deleteMemberMfa(memberId)
    .then(() => {
      dispatch(deleteMemberMfaSuccess())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const addMultiMembers = (
  params: AddMultiMembersRequest,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postImportMembers(params)
    .then((result) => {
      dispatch(postImportMembersSuccess())
      dispatch(addListMultiMembers(result.members))
      callback()
    })
    .catch((error) => {
      dispatch(setImportErrors(error))
      dispatch(setErrors(error))
      errorCallback()
    })
}

export const fetchOccupation = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getOccupationList()
    .then((result) => {
      dispatch(getOccupationListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const inviteMembers = (
  params: PostInviteMembersRequest,
  callback?: () => void,
  errorCallback?: () => void,
): AppThunk => async (dispatch) => {
  postInviteMembers(params)
    .then(() => {
      if (callback) callback()
    })
    .catch((error) => {
      dispatch(setErrors(error))
      if (errorCallback) errorCallback()
    })
}
